import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import BlogCard from '../../components/common/BlogCard';
import classes from './styles/blog.module.css';
import BlogSlider from '../../components/common/BlogSlider';
import { blogService } from '../../services/blogService';

// ── Structured data ──────────────────────────────────────────
const BLOG_PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "FetchJobs Blog",
  "description": "Insights, stories, and expert advice on hiring, career growth, and remote work.",
  "url": "https://www.fetchjobs.co/blog",
  "isPartOf": { "@type": "WebSite", "url": "https://www.fetchjobs.co" },
  "publisher": {
    "@type": "Organization",
    "name": "FetchJobs",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.fetchjobs.co/logo.png"
    }
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.fetchjobs.co" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://www.fetchjobs.co/blog" }
    ]
  }
};

// ── Shared Helmet content (used in both loading + loaded state) ──
const BlogHelmet = () => (
  <Helmet>
    {/* Core */}
    <title>Blog – Insights on Remote Work, Hiring & Career Growth | FetchJobs</title>
    <meta name="description" content="Read the latest articles on hiring best practices, career advice, and remote work insights from the FetchJobs team and industry experts." />

    {/* Canonical */}
    <link rel="canonical" href="https://www.fetchjobs.co/blog" />

    {/* Open Graph */}
    <meta property="og:type"        content="website" />
    <meta property="og:title"       content="Blog – Insights on Remote Work, Hiring & Career Growth | FetchJobs" />
    <meta property="og:description" content="Read the latest articles on hiring best practices, career advice, and remote work insights from the FetchJobs team and industry experts." />
    <meta property="og:url"         content="https://www.fetchjobs.co/blog" />
    <meta property="og:site_name"   content="FetchJobs" />
    <meta property="og:image"       content="https://www.fetchjobs.co/logo.png" />

    {/* Twitter Card */}
    <meta name="twitter:card"        content="summary" />
    <meta name="twitter:title"       content="Blog – Insights on Remote Work, Hiring & Career Growth | FetchJobs" />
    <meta name="twitter:description" content="Read the latest articles on hiring best practices, career advice, and remote work insights from the FetchJobs team and industry experts." />
    <meta name="twitter:image"       content="https://www.fetchjobs.co/logo.png" />

    {/* Robots */}
    <meta name="robots" content="index, follow" />

    {/* Blog schema */}
    <script type="application/ld+json">
      {JSON.stringify(BLOG_PAGE_SCHEMA)}
    </script>
  </Helmet>
);

// ── Component ────────────────────────────────────────────────
const Blog = () => {
  const [allBlogs, setAllBlogs]             = useState([]);
  const [displayedBlogs, setDisplayedBlogs] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [loadingMore, setLoadingMore]       = useState(false);
  const [searchTerm, setSearchTerm]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 6, total: 0, pages: 1 });
  const [hasMore, setHasMore]       = useState(true);
  const lastBlogRef = useRef();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchBlogs = useCallback(async (page, reset = false) => {
    reset ? setLoading(true) : setLoadingMore(true);

    const result = await blogService.getBlogs(page, 6, debouncedSearch);

    if (result.success) {
      if (reset) {
        setAllBlogs(result.blogs);
        setDisplayedBlogs(result.blogs);
      } else {
        setAllBlogs(prev => [...prev, ...result.blogs]);
        setDisplayedBlogs(prev => [...prev, ...result.blogs]);
      }
      setPagination(result.pagination);
      setHasMore(page < result.pagination.pages);
    } else {
      console.log(result.error || 'Failed to load blogs');
    }

    reset ? setLoading(false) : setLoadingMore(false);
  }, [debouncedSearch]);

  useEffect(() => { fetchBlogs(1, true); }, [fetchBlogs]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) fetchBlogs(pagination.page + 1, false);
  }, [loadingMore, hasMore, loading, pagination.page, fetchBlogs]);

  useEffect(() => {
    if (loading || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && hasMore && !loadingMore) loadMore(); },
      { root: null, rootMargin: '100px', threshold: 0.1 }
    );
    const currentRef = lastBlogRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); observer.disconnect(); };
  }, [loading, loadingMore, hasMore, loadMore, displayedBlogs.length]);

  const handleSearch  = (e) => setSearchTerm(e.target.value);
  const clearSearch   = () => { setSearchTerm(''); setDebouncedSearch(''); };

  const featuredBlogs = allBlogs.slice(0, 5);

  if (loading && allBlogs.length === 0) {
    return (
      <div className={classes.container}>
        <BlogHelmet />
        <div className={classes.loadingContainer}>
          <div className={classes.loader}></div>
          <p>Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <BlogHelmet />

      <div className={classes.header}>
        <h1 className={classes.title}>Our Blog</h1>
        <p className={classes.subtitle}>
          Insights, stories, and expert advice on hiring, career growth, and remote work
        </p>
      </div>

      {/* Search Bar */}
      <div className={classes.searchContainer}>
        <div className={classes.searchWrapper}>
          <svg className={classes.searchIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="8" stroke="#666" strokeWidth="2"/>
            <path d="M21 21L17 17" stroke="#666" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="search"
            placeholder="Search articles by title, content, and summary..."
            value={searchTerm}
            onChange={handleSearch}
            className={classes.searchInput}
            aria-label="Search blog articles"
          />
          {searchTerm && (
            <button onClick={clearSearch} className={classes.clearBtn} aria-label="Clear search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 6L6 18M6 6L18 18" stroke="#666" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Featured Slider */}
      {featuredBlogs.length > 0 && !searchTerm && (
        <BlogSlider blogs={featuredBlogs} />
      )}

      {/* Blog Listing */}
      <div className={classes.listingSection}>
        {displayedBlogs.length === 0 ? (
          <div className={classes.emptyContainer}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="8" stroke="#004AA6" strokeWidth="2"/>
              <path d="M21 21L17 17" stroke="#004AA6" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <h3>No articles found</h3>
            <p>Try adjusting your search or browse all articles</p>
            {searchTerm && (
              <button onClick={clearSearch} className={classes.clearSearchBtn}>Clear Search</button>
            )}
          </div>
        ) : (
          <>
            <div className={classes.blogsGrid}>
              {displayedBlogs.map((blog, index) => (
                <div
                  key={blog._id}
                  ref={index === displayedBlogs.length - 1 ? lastBlogRef : null}
                >
                  <BlogCard blog={blog} />
                </div>
              ))}
            </div>

            {loadingMore && (
              <div className={classes.loadingMore}>
                <div className={classes.loaderSmall}></div>
                <p>Loading more articles...</p>
              </div>
            )}

            {!hasMore && displayedBlogs.length > 0 && (
              <div className={classes.endMessage}>
                <p>You've reached the end! Check back for more articles.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Blog;